// Wave 12 — Settings → Security panel.
//
// Drives the four 2FA endpoints in the FastAPI backend:
//   POST /api/auth/2fa/setup    → secret + otpauth URI + backup codes
//   POST /api/auth/2fa/verify   → activate (code required)
//   POST /api/auth/2fa/login    → second-factor exchange (login flow)
//   POST /api/auth/2fa/disable  → turn 2FA off (code required)
//
// We deliberately render the QR client-side from the otpauth URI so
// the backend doesn't have to ship Pillow / qrcode. The URI itself is
// shown alongside the QR for users whose authenticator can't scan.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ShieldCheck, ShieldOff, Copy } from 'lucide-react';

function qrSrc(uri) {
  // Use a public QR-rendering endpoint via google charts / a CSP-friendly
  // fallback. Operator can swap to a local renderer if outbound calls
  // are blocked.
  return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(uri)}`;
}

export default function SecuritySettings() {
  const { t } = useT();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_uri, backup_codes }
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auth/me`);
      setMe(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to load profile'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(r.data);
      toast.success(t('Scan the QR code with your authenticator app'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Setup failed'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!verifyCode.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/2fa/verify`, { code: verifyCode.trim() });
      toast.success(t('2FA enabled'));
      setSetupData(null);
      setVerifyCode('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Invalid 2FA code'));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!disableCode.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code: disableCode.trim() });
      toast.success(t('2FA disabled'));
      setDisableCode('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Disable failed'));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => toast.success(t('Copied')));
  };

  if (loading) return null;

  return (
    <div className="xp-page">
      <Card data-testid="security-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            {t('Two-factor authentication')}
          </CardTitle>
          <CardDescription>
            {t(
              'Protect your account with a TOTP code from an authenticator app. Backup codes let you log in if you lose your device.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {me?.totp_enabled ? (
            <div className="space-y-3">
              <Badge>{t('2FA is currently enabled')}</Badge>
              <div className="grid gap-2 max-w-sm">
                <Label>{t('Disable 2FA — enter a current code')}</Label>
                <Input
                  data-testid="disable-2fa-code"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  placeholder="123456 or BACKUP-CODE9"
                />
                <Button
                  variant="destructive"
                  onClick={disable}
                  disabled={busy || !disableCode.trim()}
                  data-testid="disable-2fa-submit"
                >
                  <ShieldOff className="w-4 h-4 mr-1" />
                  {t('Disable 2FA')}
                </Button>
              </div>
            </div>
          ) : setupData ? (
            <div className="space-y-3">
              <div className="flex flex-col md:flex-row gap-4 items-start">
                <img
                  src={qrSrc(setupData.otpauth_uri)}
                  alt="TOTP QR"
                  width={200}
                  height={200}
                  data-testid="totp-qr"
                />
                <div className="space-y-2 text-sm">
                  <div className="font-medium">{t('Manual entry secret')}</div>
                  <div className="font-mono break-all">
                    {setupData.secret}
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('Copy secret')}
                      title={t('Copy secret')}
                      onClick={() => copy(setupData.secret)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="font-medium pt-2">{t('Backup codes (save now)')}</div>
                  <ul
                    className="grid grid-cols-2 gap-1 font-mono text-xs"
                    data-testid="backup-codes-list"
                  >
                    {setupData.backup_codes.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="grid gap-2 max-w-sm pt-4">
                <Label>{t('Enter a code from the app to confirm')}</Label>
                <Input
                  data-testid="verify-2fa-code"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  placeholder="123456"
                  inputMode="numeric"
                />
                <Button
                  onClick={verify}
                  disabled={busy || verifyCode.trim().length !== 6}
                  data-testid="verify-2fa-submit"
                >
                  {t('Activate 2FA')}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={startSetup}
              disabled={busy}
              data-testid="start-2fa-setup"
            >
              {t('Enable 2FA')}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
